import Enumerable from "linq";
import { Store } from "vuex";
import { State } from "../../store/state";
import { DomainRegistry } from "../../domain";
import { IConfiguration } from "../../domain/configuration";
import { WebStorageAccessor } from "../../store/WebStorageAccessor";
import { LoggerFactory } from "../../utilities/logger";
import { PolyfillLoader } from "./PolyfillLoader";
import { SessionStorageAccessor } from "../../store/SessionStorageAccessor";

export class ApplicationInitializer {
  // DomainRegistryのインスタンスは外から差し込めるようにしたが、
  // ApplicationInitializer内でstaticにDomainRegistry.instanceにアクセスしてないと実行時にundefinedのgetLoggerアクセスでエラーが出る.
  // (mapがおかしくなってるのかコンソールのログからも発生してる位置が分からない。
  // LoggerFactory.getLoggerはstaticメソッドなのでundefinedとか本来ありえないと思うのですが。。。)
  // initialize前にDomainRegistry.instanceにアクセスするとかいろいろ試したがどうにも回避できないのでstaticメンバで保持するよう暫定対策する。
  // このおまじないがあってもLoggerFactoryをDomainRegistryより先にimportすると同様の問題が起きるのでimport先のコードに問題ある可能性あり？
  // TODO: 余力が出来たらちゃんとした対策を講じること。(javascriptにトランスパイルされたコードとか追わないと解決しないかも。。。)
  protected static readonly _omajinai = DomainRegistry.instance;

  static async initialize<TConfiguration extends IConfiguration = IConfiguration>(store: Store<State>, registry: DomainRegistry<TConfiguration>): Promise<void> {
    PolyfillLoader.load();
    LoggerFactory.initialize(); // 一旦デフォルトで初期化.
    try {
      this.loadHostName(store);
      this.loadClientIpAddress(store);
      await this.loadConfiguration(store, registry);
    } catch (e) {
      LoggerFactory.getLogger().error("設定の取得に失敗.");
      LoggerFactory.getLogger().error(e);
    }
  }

  private static loadHostName(store: Store<State>): string {
    this.initializeHostInformation(store, this.readLocationParameter("hostName"));
    return store.getters.hostName;
  }
  
  private static loadClientIpAddress(store: Store<State>): string {
    this.initializeClientIpAddressInformation(store, this.readLocationParameter("clientIpAddress"));
    return store.getters.clientIpAddress;
  }
  
  private static readLocationParameter(key: string): string {
    const found = Enumerable.from(location.search.substring(1).split("&"))
      .select(p => p.split("="))
      .where(p => !!p && (1 < p.length) && (p[0].toUpperCase() === key.toUpperCase()))
      .select(p => {
        return {
          key: p[0],
          value: p[1]
        };
      })
      .firstOrDefault();
    return found
      ? decodeURIComponent(found.value) //  パラメータは一律デコードする.(不要な場合にやっても悪影響はないので.)
      : null;
  }
  
  private static initializeHostInformation(store: Store<State>, hotsNameFromUrl: string): void {
    if (hotsNameFromUrl) {
      LoggerFactory.getLogger().info("端末情報がURLに指定されています.(" + hotsNameFromUrl + ")");
      if (!store.getters.hostName || (store.getters.hostName !== hotsNameFromUrl)) {
        store.commit("setHostName", hotsNameFromUrl);
      }
    } else if (WebStorageAccessor.hostName) {
      LoggerFactory.getLogger().info("端末情報が登録されています.(" + WebStorageAccessor.hostName + ")");
      if (!store.getters.hostName || (store.getters.hostName !== WebStorageAccessor.hostName)) {
        store.commit("setHostName", WebStorageAccessor.hostName);
      }
    }
  }

  private static initializeClientIpAddressInformation(store: Store<State>, clientIpAddressFromUrl: string): void {
    if (clientIpAddressFromUrl) {
      LoggerFactory.getLogger().info("端末のIP情報がURLに指定されています.(" + clientIpAddressFromUrl + ")");
      if (!store.getters.clientIpAddress || (store.getters.clientIpAddress !== clientIpAddressFromUrl)) {
        store.commit("setClientIpAddress", clientIpAddressFromUrl);
      }
    } else if (SessionStorageAccessor.clientIpAddress) {
      LoggerFactory.getLogger().info("端末のIP情報が登録されています.(" + SessionStorageAccessor.clientIpAddress + ")");
      if (!store.getters.clientIpAddress || (store.getters.clientIpAddress !== SessionStorageAccessor.clientIpAddress)) {
        store.commit("setClientIpAddress", SessionStorageAccessor.clientIpAddress);
      }
    }
  }
  
  private static async loadConfiguration<TConfiguration extends IConfiguration = IConfiguration>(store: Store<State>, registry: DomainRegistry<TConfiguration>): Promise<void> {
    const service = registry.configuration.getConfigurationService();
    await service.load();
    LoggerFactory.initialize(registry.configuration.getConfigurationService().getConfiguration());  // 設定に従って初期化.
  }
}
